import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Map, { Layer, Marker, NavigationControl, Source, type MapRef, type MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Play, Pause, Square, Navigation, Flag, AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { haversineM, bearingDeg, compassPt, type RouteCoord } from '@/stores/navRoutes';
import { navigationApi } from '@/api/endpoints/navigation';
import { useEntitlements } from '@/stores/entitlements';

const STYLE: StyleSpecification = {
  version: 8,
  sources: {
    base: { type: 'raster', tiles: ['https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png', 'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png'], tileSize: 256, attribution: '© CARTO' },
  },
  layers: [{ id: 'base', type: 'raster', source: 'base' }],
};

interface Dest { lat: number; lon: number; name?: string }

export function NavigatorPage() {
  const [params] = useSearchParams();
  const mapRef = useRef<MapRef | null>(null);
  const watchId = useRef<number | null>(null);
  const sessionId = useRef<number | null>(null);

  const premium = useEntitlements((s) => s.isEnabled('navegador'));
  const pausedMs = useRef(0);
  const pauseStart = useRef<number | null>(null);

  const [coords, setCoords] = useState<RouteCoord[]>([]);
  const [tracking, setTracking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [dest, setDest] = useState<Dest | null>(() => {
    const dlat = params.get('dlat'); const dlon = params.get('dlon');
    return dlat && dlon ? { lat: Number(dlat), lon: Number(dlon), name: params.get('dname') ?? undefined } : null;
  });

  // relógio para tempo de navegação (congela quando pausado)
  useEffect(() => {
    if (!tracking || paused) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [tracking, paused]);

  const stopWatch = () => {
    if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
  };
  useEffect(() => () => stopWatch(), []);

  const startWatch = () => {
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setAccuracy(pos.coords.accuracy);
        const c: RouteCoord = {
          lat: pos.coords.latitude, lon: pos.coords.longitude, t: pos.timestamp,
          speed: pos.coords.speed != null && pos.coords.speed >= 0 ? pos.coords.speed * 1.94384 : undefined,
        };
        setCoords((arr) => [...arr, c]);
        mapRef.current?.easeTo({ center: [c.lon, c.lat], duration: 500 });
      },
      (err) => setError(err.code === 1 ? 'Permissão de GPS negada.' : 'Falha ao obter posição (GPS).'),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 },
    );
  };

  const start = async () => {
    if (!premium) { setError('Navegador Premium não habilitado para sua conta.'); return; }
    if (!('geolocation' in navigator)) { setError('GPS não disponível neste dispositivo.'); return; }
    setError(null); setCoords([]); setStartedAt(Date.now()); setTracking(true); setPaused(false);
    pausedMs.current = 0; pauseStart.current = null;
    try {
      const s = await navigationApi.start({
        route_name: dest?.name ? `Até ${dest.name}` : null,
        origin: 'app',
        destination_latitude: dest?.lat ?? null,
        destination_longitude: dest?.lon ?? null,
      });
      sessionId.current = s.id;
    } catch {
      sessionId.current = null; // segue desenhando local; salva no finish se possível
    }
    startWatch();
  };

  const pause = () => { stopWatch(); pauseStart.current = Date.now(); setPaused(true); };
  const resume = () => {
    if (pauseStart.current != null) { pausedMs.current += Date.now() - pauseStart.current; pauseStart.current = null; }
    setPaused(false); setNow(Date.now()); startWatch();
  };

  const finish = async () => {
    stopWatch(); setTracking(false); setPaused(false);
    const sid = sessionId.current;
    if (sid && coords.length >= 1) {
      setSaving(true);
      try {
        await navigationApi.update(
          sid,
          coords.map((c) => ({
            latitude: c.lat, longitude: c.lon, speed: c.speed ?? null,
            recorded_at: new Date(c.t).toISOString(),
          })),
        );
        await navigationApi.finish(sid);
      } catch {
        setError('Não foi possível salvar a rota no servidor.');
      } finally {
        setSaving(false);
      }
    }
    sessionId.current = null;
    setStartedAt(null);
  };

  const onMapClick = (e: MapLayerMouseEvent) => {
    if (tracking) return;
    setDest({ lat: e.lngLat.lat, lon: e.lngLat.lng });
  };

  const cur = coords[coords.length - 1] ?? null;
  const pathGeoJson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: [{ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: coords.map((c) => [c.lon, c.lat]) } }],
  }), [coords]);

  const distTraveled = useMemo(() => {
    let d = 0; for (let i = 1; i < coords.length; i++) d += haversineM(coords[i - 1], coords[i]); return d;
  }, [coords]);

  const toDest = useMemo(() => {
    if (!cur || !dest) return null;
    const m = haversineM(cur, dest);
    return { nm: m / 1852, dir: compassPt(bearingDeg(cur, dest)) };
  }, [cur, dest]);

  const elapsed = startedAt ? Math.max(0, Math.floor((now - startedAt - pausedMs.current) / 1000)) : 0;
  const fmtTime = (s: number) => `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const curKn = cur?.speed ?? 0;

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1">
        <Map
          ref={mapRef}
          initialViewState={{ longitude: dest?.lon ?? -43.16, latitude: dest?.lat ?? -22.9, zoom: 13 }}
          mapStyle={STYLE}
          style={{ width: '100%', height: '100%' }}
          attributionControl={false}
          onClick={onMapClick}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <Source id="seamark" type="raster" tiles={['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png']} tileSize={256}>
            <Layer id="seamark-l" type="raster" paint={{ 'raster-opacity': 0.9 }} />
          </Source>
          {coords.length > 1 && (
            <Source id="path" type="geojson" data={pathGeoJson}>
              <Layer id="path-l" type="line" paint={{ 'line-color': '#007AFF', 'line-width': 4 }} />
            </Source>
          )}
          {dest && (
            <Marker longitude={dest.lon} latitude={dest.lat} anchor="bottom">
              <Flag className="h-7 w-7 text-status-blocked drop-shadow" />
            </Marker>
          )}
          {cur && (
            <Marker longitude={cur.lon} latitude={cur.lat} anchor="center">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-white shadow ring-2 ring-white">
                <Navigation className="h-4 w-4" />
              </span>
            </Marker>
          )}
        </Map>

        <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-card/95 px-3 py-1 text-xs font-semibold shadow ring-1 ring-border">
          {tracking ? (paused ? 'Pausado' : 'Navegando…') : dest ? 'Destino definido — toque em Iniciar' : 'Toque no mapa para definir destino'}
        </div>
        {error && (
          <div className="absolute left-3 right-3 top-12 flex items-center gap-2 rounded-md bg-status-blocked/15 px-3 py-2 text-xs font-semibold text-status-blocked">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}
        {tracking && accuracy != null && accuracy > 30 && (
          <div className="absolute left-3 right-3 bottom-3 rounded-md bg-status-ready/15 px-3 py-1.5 text-[11px] font-semibold text-status-ready">
            Precisão do GPS baixa (±{Math.round(accuracy)} m)
          </div>
        )}
      </div>

      {/* Painel de métricas + controles */}
      <div className="border-t border-border bg-card p-3">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          <Metric label="Velocidade" value={`${curKn.toFixed(1).replace('.', ',')} nós`} />
          <Metric label="Rumo" value={cur?.speed != null && coords.length > 1 ? `${compassPt(bearingDeg(coords[coords.length - 2], cur))}` : '—'} />
          <Metric label="Tempo" value={fmtTime(elapsed)} />
          <Metric label="Percorrido" value={`${(distTraveled / 1852).toFixed(2).replace('.', ',')} mn`} />
          <Metric label="Destino" value={toDest ? `${toDest.nm.toFixed(1).replace('.', ',')} mn` : '—'} sub={toDest ? `a ${toDest.dir}` : dest?.name} />
          <Metric label="Pontos" value={String(coords.length)} />
        </div>
        <div className="mt-3 flex gap-2">
          {!premium ? (
            <div className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-secondary px-3 py-2 text-xs font-semibold text-muted-foreground">
              <Lock className="h-4 w-4" /> Navegador Premium — fale com sua revenda/administrador
            </div>
          ) : !tracking ? (
            <Button onClick={start} className="flex-1 gap-1.5"><Play className="h-4 w-4" /> Iniciar navegação</Button>
          ) : (
            <>
              {!paused ? (
                <Button variant="outline" onClick={pause} className="gap-1.5"><Pause className="h-4 w-4" /> Pausar</Button>
              ) : (
                <Button variant="outline" onClick={resume} className="gap-1.5"><Play className="h-4 w-4" /> Retomar</Button>
              )}
              <Button onClick={finish} variant="destructive" disabled={saving} className="flex-1 gap-1.5"><Square className="h-4 w-4" /> {saving ? 'Salvando…' : 'Finalizar e salvar'}</Button>
            </>
          )}
          {dest && !tracking && premium && (
            <Button variant="outline" onClick={() => setDest(null)}>Limpar destino</Button>
          )}
        </div>
        <p className="mt-2 text-center text-[10px] text-muted-foreground">
          Posição do <b>smartphone/navegador</b> (Rota via App) — distinta do rastreador da embarcação.
        </p>
      </div>
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg bg-secondary/60 p-2 text-center">
      <p className="text-[10px] font-medium text-muted-foreground">{label}</p>
      <p className="text-sm font-bold leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}
